import { useMemo, useState } from "react";
import { useTable } from "react-table";
import {
  defaultDateDisplay,
  getUrlQuerysection,
  truncateString,
} from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import {
  useGetAllProductsList,
  useGetProductDetails,
  useGetRestockDetails,
} from "../../../../hooks/api/queries";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { v4 as uuidv4 } from "uuid";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import secondaryComponents from "../..";

const useRestockLogsTable = ({ stockHistory }) => {
  const navigate = useNavigate();
  const [selectedProductID, setSelectedProductID] = useState({
    view: null,
    add: null,
  });
  const queries = getUrlQuerysection();
  const [openAddStockWidget, setOpenAddStockWidget] = useState({
    view: null,
    add: null,
  });
  const columns = useMemo(
    () => [
      {
        Header: "Date/Time",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => <span>{defaultDateDisplay(value)}</span>,
      },
      {
        Header: "Supplier",
        accessor: "suppliers",
        Cell: ({ cell: { value } }) => {
          if (!value?.length) {
            return <span className="text-muted">N/A</span>;
          }

          const handleClick = (id) => {
            navigate(pathConstants.INVENTORY_SUPPLIERS_DETAILS({ id }));
          };

          if (value?.length > 1) {
            return (
              <secondaryComponents.ToolTip
                placeholderContent="Multiple Suppliers"
                showInfoIcon
                useContentStyling
                useWideContent
                contentStyles={{
                  width: "100%",
                  minWidth: "200px",
                  maxWidth: "300px",
                  padding: "15px",
                }}
              >
                <span
                  className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pb-2`}
                >
                  <p className="smallest-text-size">Supplier Name</p>
                </span>

                {value?.map((supplier) => (
                  <div
                    className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pt-2 pb-2 cursor-pointer`}
                    key={uuidv4()}
                    onClick={() => handleClick(supplier?.id)}
                  >
                    <p className="smallest-text-size purple--text">
                      {truncateString(supplier?.name, 20)}
                    </p>
                  </div>
                ))}
              </secondaryComponents.ToolTip>
            );
          }
          const supplier = value[0];
          return (
            <span
              className="purple--text cursor-pointer"
              onClick={() => handleClick(supplier?.id)}
            >
              {supplier?.name || "N/A"}
            </span>
          );
        },
      },
      {
        Header: "PO Number",
        accessor: "purchase_order",
        Cell: ({ cell: { value } }) =>
          value ? (
            <Link
              className="purple--text smallest-text-size"
              to={pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
                id: value?.id,
              })}
            >
              {value?.po_number}
            </Link>
          ) : (
            "N/A"
          ),
      },
      {
        Header: "GR Number",
        accessor: "goods_received",
        Cell: ({ cell: { value } }) =>
          value ? (
            <Link
              className="purple--text smallest-text-size"
              to={pathConstants.INVENTORY_GOODS_RECEIVED_DETAILS({
                id: value?.id,
              })}
            >
              {value?.gr_number}
            </Link>
          ) : (
            "N/A"
          ),
      },
      {
        Header: "Restock Type",
        accessor: "type",
        Cell: ({ cell: { value } }) => (
          <span className="text-muted">
            {value === "BULK" ? "Bulk Restock" : "Single Restock"}
          </span>
        ),
      },
      {
        Header: "Store",
        accessor: "stores",
        Cell: ({ cell: { value: stores } }) => {
          if (stores?.length > 1) {
            return (
              <span>
                <secondaryComponents.ToolTip
                  placeholderContent="Multiple Stores"
                  showInfoIcon
                  useContentStyling
                  contentStyles={{
                    width: "100%",
                    maxWidth: "200px",
                    padding: "15px",
                  }}
                >
                  <span
                    className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pb-2`}
                  >
                    <p className="smallest-text-size">Store Name</p>
                  </span>
                  <span>
                    {stores.map((store) => (
                      <div
                        className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pt-2 pb-2`}
                        key={uuidv4()}
                      >
                        <p className="smallest-text-size">
                          {truncateString(store?.name, 20)}
                        </p>
                      </div>
                    ))}
                  </span>
                </secondaryComponents.ToolTip>
              </span>
            );
          }
          return (
            <span className="text-muted">{stores?.[0]?.name || "N/A"}</span>
          );
        },
      },

      {
        Header: "Action",
        Cell: ({ row }) => (
          <primaryComponents.Button
            onClick={() => {
              setSelectedProductID((prev) => ({
                ...prev,
                view: row?.original?.id,
              }));
              setOpenAddStockWidget((prev) => ({
                ...prev,
                view: row?.original?.id,
              }));
            }}
            classNames={"btn btn--primary smallBtn"}
          >
            View
          </primaryComponents.Button>
        ),
      },
    ],
    [],
  );
  const {
    isLoading: isFetchingViewProductDetails,
    data: viewSelectedProduct,
    refetch: refetchViewSelectedProduct,
  } = useGetRestockDetails({ id: selectedProductID.view });

  const {
    isLoading: isFetchingProductDetails,
    data: selectedProduct,
    refetch: refetchSelectedProduct,
  } = useGetProductDetails({ id: selectedProductID.add });

  const {
    isLoading: isFetchingProducts,
    data: products,
    refetch: handleFetchproducts,
  } = useGetAllProductsList({ queries });

  const instance = useTable({
    columns: columns,
    data: stockHistory,
  });

  return {
    instance,
    isFetchingProductDetails,
    selectedProduct,
    refetchSelectedProduct,
    openAddStockWidget,
    setOpenAddStockWidget,
    isFetchingViewProductDetails,
    viewSelectedProduct,
    refetchViewSelectedProduct,
    handleFetchproducts,
    isFetchingProducts,
    products,
  };
};

export default useRestockLogsTable;
